// src/components/auth/AuthShell.jsx
// Shell visual compartido por Login, Register, ForgotPassword y ResetPassword.
// Provee el header navy con logo + el contenedor del card. Las clases CSS
// (auth-title, auth-input, auth-submit, etc.) están disponibles para los
// children sin importar nada extra.
import React from 'react';


const AUTH_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.auth-root {
    min-height: 100vh;
    background:
        radial-gradient(1200px 600px at 10% 10%, rgba(10,74,168,.06), transparent 60%),
        radial-gradient(1000px 500px at 90% 90%, rgba(243,201,75,.06), transparent 60%),
        var(--c-bg);
    display: flex; align-items: center; justify-content: center;
    padding: 24px 16px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    color: var(--c-text-1);
}
.auth-root * { box-sizing: border-box; }

.auth-card {
    width: 100%; max-width: 440px;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(8, 29, 73, .14), 0 4px 12px rgba(8, 29, 73, .08);
    border: 1px solid var(--c-border);
    background: var(--c-card);
    animation: auth-rise .45s cubic-bezier(.2,.7,.3,1) both;
}
@keyframes auth-rise {
    from { opacity: 0; transform: translateY(12px) scale(.98); }
    to   { opacity: 1; transform: none; }
}

.auth-header {
    background:
        radial-gradient(circle at 1px 1px, rgba(255,255,255,.07) 1px, transparent 0) 0 0 / 22px 22px,
        linear-gradient(160deg, var(--brand-900, #062a63) 0%, var(--brand-800, #07357f) 60%, var(--brand-700, #0a4aa8) 100%);
    padding: 36px 36px 30px;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
    position: relative;
}
.auth-header::after {
    content: ''; position: absolute; left: 0; right: 0; bottom: 0;
    height: 4px;
    background: linear-gradient(90deg, transparent 0%, var(--accent-amber, #f3c94b) 30%, var(--accent-amber, #f3c94b) 70%, transparent 100%);
}
.auth-logo {
    width: 100%; max-width: 240px; height: auto;
    filter: drop-shadow(0 2px 8px rgba(0,0,0,.15));
}
.auth-accent-line {
    width: 44px; height: 3px;
    background: var(--accent-amber, #f3c94b);
    border-radius: 2px;
}
.auth-eyebrow {
    margin: 0;
    color: rgba(255,255,255,.7);
    font-size: 11px; letter-spacing: 2px;
    text-transform: uppercase; font-weight: 600;
}

.auth-body { padding: 32px 36px 36px; }

.auth-title {
    font-size: 22px; font-weight: 700; color: var(--c-text-1);
    margin: 0 0 4px; letter-spacing: -0.01em;
}
.auth-subtitle {
    color: var(--c-text-3); font-size: 13.5px;
    margin: 0 0 24px; line-height: 1.5;
}

.auth-field { margin-bottom: 16px; }
.auth-field-header {
    display: flex; justify-content: space-between; align-items: baseline;
    margin-bottom: 6px;
}
.auth-label {
    font-size: 12px; font-weight: 600; color: var(--c-text-2);
    letter-spacing: 0.1px;
}
.auth-link {
    font-size: 12px; color: var(--brand-700, #0a4aa8);
    text-decoration: none; font-weight: 600;
    transition: color .15s;
}
.auth-link:hover { color: var(--brand-800, #07357f); text-decoration: underline; }

.auth-input-wrap { position: relative; }
.auth-input {
    width: 100%; padding: 11px 13px;
    font-size: 14px; font-family: inherit;
    border: 1px solid var(--c-border);
    border-radius: 10px;
    background: var(--c-input-bg);
    color: var(--c-text-1);
    outline: none;
    transition: border-color .15s, box-shadow .15s, background .15s;
}
.auth-input:focus {
    border-color: var(--brand-700, #0a4aa8);
    box-shadow: 0 0 0 3px rgba(10,74,168,.12);
    background: var(--c-card);
}
.auth-input:disabled {
    background: var(--c-msg-bg); color: var(--c-text-3); cursor: not-allowed;
}
.auth-input.has-toggle { padding-right: 44px; }

.auth-toggle {
    position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer;
    color: var(--c-text-4); padding: 6px; line-height: 1;
    border-radius: 6px; display: inline-flex; align-items: center;
    transition: color .15s, background .15s;
}
.auth-toggle:hover { color: var(--c-text-2); background: var(--c-msg-bg); }

.auth-submit {
    width: 100%;
    background: var(--brand-700, #0a4aa8);
    color: #fff;
    border: 1px solid var(--brand-700, #0a4aa8);
    border-radius: 10px;
    padding: 12px;
    font-size: 14.5px; font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    margin-top: 8px;
    transition: all .15s ease;
    box-shadow: 0 1px 3px rgba(10,74,168,.3);
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.auth-submit:hover:not(:disabled) {
    background: var(--brand-800, #07357f);
    border-color: var(--brand-800, #07357f);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(10,74,168,.3);
}
.auth-submit:disabled { opacity: 0.6; cursor: not-allowed; }

.auth-footer-link {
    text-align: center; margin-top: 18px; padding-top: 18px;
    border-top: 1px solid var(--c-card-header-border);
    font-size: 13px; color: var(--c-text-3);
}
.auth-footer-link a {
    color: var(--brand-700, #0a4aa8);
    text-decoration: none; font-weight: 700;
    margin-left: 4px;
    transition: color .15s;
}
.auth-footer-link a:hover { color: var(--brand-800, #07357f); text-decoration: underline; }

/* Estados especiales */
.auth-alert {
    background: var(--c-priority-urgent-bg);
    border: 1px solid #fecaca;
    color: #b91c1c;
    border-radius: 10px;
    padding: 11px 14px;
    font-size: 13px;
    margin-bottom: 16px;
    line-height: 1.5;
}
.auth-alert strong { display: block; margin-bottom: 2px; }

.auth-success-icon {
    width: 64px; height: 64px; border-radius: 50%;
    background: #d1fae5; border: 2px solid #6ee7b7;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 18px;
    color: #059669;
}
.auth-loading {
    text-align: center; padding: 32px 0;
}
.auth-loading p { color: var(--c-text-3); margin-top: 12px; font-size: 14px; }

.auth-role-chip {
    display: inline-flex; align-items: center;
    border-radius: 6px; padding: 2px 10px;
    font-weight: 600; font-size: 12px;
    letter-spacing: 0.1px;
}

@media (max-width: 480px) {
    .auth-header { padding: 28px 26px 24px; }
    .auth-body { padding: 26px 26px 30px; }
    .auth-title { font-size: 20px; }
}
`;


export default function AuthShell({ children, eyebrow = 'Mesa de Ayuda' }) {
    return (
        <>
            <style>{AUTH_CSS}</style>
            <div className="auth-root">
                <div className="auth-card">
                    <div className="auth-header">
                        <img src="/caprepa.svg" alt="Caprepa" className="auth-logo" />
                        <div className="auth-accent-line" />
                        <p className="auth-eyebrow">{eyebrow}</p>
                    </div>
                    <div className="auth-body">{children}</div>
                </div>
            </div>
        </>
    );
}


// Iconos compartidos por las páginas de auth
export const IconEye = ({ off }) => off ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

export const IconArrow = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

export const IconBack = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5" />
        <path d="m12 19-7-7 7-7" />
    </svg>
);

export const IconCheck = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);
